import clsx from 'clsx'
import { JSONSchema7 } from 'json-schema'
import { useCallback, useEffect } from 'react'
import ErrorDisplay from '../../../components/error/ErrorDisplay'
import Form from '../../form/ComponentForm'
import { GraphNode, NodeData, RunConfig, RunEvent, RunInfo, RunResult, Tree } from './NodeData'

import styles from './NodeRunTab.module.css'
import Loading from '../../../Loading'
import { Elements } from 'react-flow-renderer'

function getSelectedNode(nodes: Elements<any> | null): GraphNode | null {
  if (nodes !== null && nodes.length === 1 && nodes[0].data && (nodes[0].data.path || '').length > 0) {
    return nodes[0] as GraphNode
  }
  return null
}

function pathOrLabelFrom(
  data: NodeData,
  thing:
    | {
        [key: string]: any
      }
    | undefined
): any {
  if (typeof thing !== 'object' || !data.path) {
    return undefined
  }
  if ((data.label || '').length > 0) {
    const result = thing[data.label || '']
    if (result !== undefined) {
      return result
    }
  }
  return thing[data.path]
}

function eventsFor(data: NodeData, result?: RunResult) {
  let events: RunEvent[] = []
  switch (data?.kind) {
    case 'input':
      events = pathOrLabelFrom(data, result?.input_events) || []
      break
    case 'processor':
      events = pathOrLabelFrom(data, result?.processor_events) || []
      break
    case 'output':
      events = pathOrLabelFrom(data, result?.output_events) || []
      break
    default:
  }
  return events
}

function styleForType(type: string) {
  switch (type) {
    case 'CONSUME':
      return styles.eventConsume
    case 'PRODUCE':
      return styles.eventProduce
    case 'ERROR':
      return styles.eventError
    case 'DELETE':
      return styles.eventDelete
    default:
      return null
  }
}

function showEventsFor(data: NodeData, result?: RunResult) {
  const events = eventsFor(data, result)
  return (
    <>
      <h4>Events{events.length > 0 ? ` (${events.length})` : null}</h4>
      <div className={styles.eventsList}>
        {events.length === 0 ? (
          <>no events</>
        ) : (
          events.map((event, idx) => {
            return (
              <div key={idx} className={clsx(styles.eventRow, idx % 2 === 0 && styles.eventRowOdd)}>
                <div className={clsx(styles.eventType, styleForType(event.type))}>{event.type}</div>
                <pre className={styles.eventContent}>{event.content}</pre>
              </div>
            )
          })
        )}
      </div>
    </>
  )
}

function mocksFor(data: NodeData, config: RunConfig) {
  let mocks = null
  if (!data.path) {
    return mocks
  }
  switch (data?.kind) {
    case 'input':
      mocks = (config?.mock_inputs || {})[data.path] || null
      break
    case 'processor':
      mocks = (config?.mock_processors || {})[data.path] || null
      break
    default:
  }
  return mocks
}

function mocksConfigSchema(data: NodeData): {
  schema: JSONSchema7
  uiSchema: any
} | null {
  switch (data?.kind) {
    case 'input':
      return {
        schema: {
          type: 'object',
          properties: {
            messages: {
              type: 'array',
              items: {
                type: 'string',
              },
            },
          },
        },
        uiSchema: {
          messages: {
            items: {
              'ui:widget': 'bigText',
            },
          },
        },
      }
    case 'processor':
      return {
        schema: {
          type: 'object',
          properties: {
            mapping: {
              type: 'string',
            },
          },
        },
        uiSchema: {
          mapping: {
            'ui:widget': 'bloblang',
          },
        },
      }
    default:
      return null
  }
}

/**
 * Renders the run view for a configuration tree, including displaying mock behaviors and events for selected nodes.
 *
 * @param {object} props - The component's props.
 * @param {Tree} props.tree - The configuration tree to render.
 * @param {RunInfo} props.runInfo - Information about the configuration run.
 * @param {boolean} props.isRunning - Indicates whether the configuration is currently running.
 * @param {Function} props.setRunConfig - Callback to set the run configuration.
 * @param {Elements<NodeData>} props.allNodes - All nodes in the configuration tree.
 * @param {Elements<NodeData> | null} props.selectedNodes - Currently selected nodes.
 * @param {Function} props.setSelectedNodes - Callback to set selected nodes.
 * @returns {JSX.Element} The rendered component.
 */
export default function RunView({
  tree,
  runInfo,
  runError,
  isRunning,
  setRunConfig,
  allNodes,
  selectedNodes,
  setSelectedNodes,
}: {
  tree: Tree
  runInfo: RunInfo
  isRunning: boolean
  runError: Error | null
  setRunConfig: (conf: RunConfig, onFinished: () => void) => void
  allNodes: Elements<NodeData>
  selectedNodes: Elements<NodeData> | null
  setSelectedNodes: (nodes: any) => void
}) {
  // Nodes that are drawn and need action are updated to be highlighted.
  useEffect(() => {
    allNodes.forEach((n:any) => {
      if (!n.data) {
        return
      }
      if (!n.data.setNeedsAction) {
        return
      }
      switch (n.data?.kind) {
        case 'input':
          if (n.data.setEvents) {
            n.data.setEvents(pathOrLabelFrom(n.data, runInfo.result?.input_events) || [])
          }
          n.data.setNeedsAction(pathOrLabelFrom(n.data, runInfo.result?.missing_inputs) !== undefined)
          break
        case 'processor':
          if (n.data.setEvents) {
            n.data.setEvents(pathOrLabelFrom(n.data, runInfo.result?.processor_events) || [])
          }
          n.data.setNeedsAction(pathOrLabelFrom(n.data, runInfo.result?.missing_processors) !== undefined)
          break
        case 'output':
          if (n.data.setEvents) {
            n.data.setEvents(pathOrLabelFrom(n.data, runInfo.result?.output_events) || [])
          }
          n.data.setNeedsAction(false)
          break
        default:
          n.data.setNeedsAction(false)
      }
    })
  }, [allNodes, runInfo])

  const showMocksFor = useCallback(
    (node: GraphNode, config: RunConfig) => {
      const mockConfig = mocksFor(node.data, config)
      let mockSchema = null
      if (mockConfig !== null) {
        mockSchema = mocksConfigSchema(node.data)
      }
      if (mockConfig === null || mockSchema === null) {
        if (node.data.kind !== 'input' && node.data.kind !== 'processor') {
          return null
        }
        if (tree.read_only) {
          return null
        }
        return (
          <button
            onClick={() => {
              if (!node.data.path) {
                return
              }
              if (node.data?.kind === 'input') {
                if (!runInfo.config.mock_inputs) {
                  runInfo.config.mock_inputs = {}
                }
                runInfo.config.mock_inputs[node.data.path] = {
                  messages: [`{"content":"example message"}`],
                }
              } else if (node.data?.kind === 'processor') {
                if (!runInfo.config.mock_processors) {
                  runInfo.config.mock_processors = {}
                }
                runInfo.config.mock_processors[node.data.path] = {
                  mapping: `root = this`,
                }
              } else {
                return
              }
              setRunConfig(runInfo.config, () => setSelectedNodes([node]))
              setSelectedNodes([])
            }}
            className={clsx('bstdioTextBtn')}
          >
            Mock this component
          </button>
        )
      }
      const mockHelpText = ((t) => {
        switch (t) {
          case 'input':
            return 'Add one or more message contents to produce during the test run.'
          case 'processor':
            return 'Mock a processor by specifying a Bloblang mapping that translates input messages into the expected processor outcome.'
          default:
        }
        return ''
      })(node.data?.kind)
      return (
        <>
          {tree.read_only ? null : (
            <button
              onClick={() => {
                if (!runInfo.config || !node.data.path) {
                  return
                }
                if (node.data?.kind === 'input') {
                  if (!runInfo.config.mock_inputs) {
                    return
                  }
                  delete runInfo.config.mock_inputs[node.data.path]
                } else if (node.data?.kind === 'processor') {
                  if (!runInfo.config.mock_processors) {
                    return
                  }
                  delete runInfo.config.mock_processors[node.data.path]
                } else {
                  return
                }
                setSelectedNodes([])
                setRunConfig(runInfo.config, () => {
                  //
                })
              }}
              className={clsx('bstdioTextBtn')}
            >
              Remove mock behaviour
            </button>
          )}
          <h4>Mock Behaviour</h4>
          {mockHelpText ? <pre className={styles.mockHelpText}>{mockHelpText}</pre> : null}
          <Form
            schema={mockSchema.schema}
            uiSchema={mockSchema.uiSchema}
            formData={mockConfig}
            onChange={() => {
              /* */
            }}
            disabled={tree.read_only}
            onSubmit={(ctx) => {
              if (!node.data.path) {
                return
              }
              if (node.data?.kind === 'input') {
                if (!runInfo.config.mock_inputs) {
                  runInfo.config.mock_inputs = {}
                }
                runInfo.config.mock_inputs[node.data.path] = ctx.formData
              } else if (node.data?.kind === 'processor') {
                if (!runInfo.config.mock_processors) {
                  runInfo.config.mock_processors = {}
                }
                runInfo.config.mock_processors[node.data.path] = ctx.formData
              } else {
                return
              }
              setSelectedNodes([])
              setRunConfig(runInfo.config, () => {
                //
              })
            }}
            onCancel={() => setSelectedNodes([])}
          />
        </>
      )
    },
    [runInfo, tree.read_only, setSelectedNodes, setRunConfig]
  )

  const node = getSelectedNode(selectedNodes)
  return (
    <div className={styles.runViewContent}>
      {/* RUN REQUEST FAILED */}
      {runError !== null ? <ErrorDisplay err={runError} /> : null}

      {/* RUN NOT FINISHED */}
      {isRunning ? <Loading action={'Running config...'} /> : null}

      {/* NODE SELECTED AND RUN FINISHED */}
      {runInfo !== null && node?.data ? (
        <div>
          {showMocksFor(node, runInfo.config)}
          {showEventsFor(node.data, runInfo.result)}
        </div>
      ) : null}
    </div>
  )
}
